/**
 * MarketingAudiencesPage — the audience registry.
 *
 * Reads GET /api/admin/segments, backed by the `dynamic_segments` table.
 * (An earlier version of this header described an `audiences` table and a
 * planned /api/admin/audiences endpoint; neither is real — the tables of that
 * name in production have no migration and no reader, and that endpoint
 * returns an incompatible shape. Do not "finish" that migration.)
 *
 * Audiences whose rule mirrors a nurture sequence carry `linked_flow_slug`
 * (migration 1102) and are pinned to the top, because those are the ones an
 * operator can reason about: the count is exactly who that sequence will enrol.
 *
 * The member count means DELIVERABLE reach — the API excludes the suppression
 * list and the marketing opt-out, which is what the subtitle has always
 * claimed and, until 19 August 2026, never did.
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PillarPageShell from "../shared/PillarPageShell";
import {
  ActionButton,
  StarterChooser,
  type StarterEntry,
} from "../../ops";
import { EmptyState, HelpTip } from "../../help";
import apiService from "../../../services/api";
import SegmentBuilderDialog, {
  type SegmentBuilderStarterSeed,
} from "../../communications/audience/SegmentBuilderDialog";
import type { SegmentBuilderInitialFilter } from "../../communications/SegmentBuilder";

interface AudienceRow {
  id: string;
  slug: string;
  name: string;
  description?: string;
  is_system: boolean;
  is_active: boolean;
  last_count: number | null;
  reachable_count?: number | null;
  last_refreshed_at?: string | null;
  category?: string;
  linked_flow_slug?: string | null;
  linked_flow_name?: string | null;
  linked_flow_status?: string | null;
}

const FILTERS = [
  { id: "all", label: "All" },
  { id: "sequence", label: "🎯 Feeds a sequence" },
  { id: "system", label: "🔒 System (pre-built)" },
  { id: "custom", label: "⭐ Custom" },
  { id: "archived", label: "🗄 Archived" },
];

/** Marketing number of the sequence an audience feeds, e.g. "F1". */
const flowBadge = (name?: string | null): string => {
  const m = (name || "").match(/^([FP]\d+[a-z]?)/);
  return m ? m[1] : "";
};

function isFilterGroup(v: unknown): v is SegmentBuilderInitialFilter {
  if (!v || typeof v !== "object") return false;
  const o = v as { op?: unknown; conditions?: unknown };
  return (
    (o.op === "AND" || o.op === "OR") &&
    Array.isArray(o.conditions) &&
    o.conditions.length > 0
  );
}

/** Ensures resolver-valid AND/OR node (blank starter used empty conditions). */
function normalizeStarterFilter(raw: unknown): SegmentBuilderInitialFilter {
  if (isFilterGroup(raw)) return raw;
  return {
    op: "AND",
    conditions: [{ field: "users.is_active", op: "eq", value: true }],
  };
}

const MarketingAudiencesPage: React.FC = () => {
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState<AudienceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<
    "all" | "sequence" | "system" | "custom" | "archived"
  >("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);
  const [segmentBuilderOpen, setSegmentBuilderOpen] = useState(false);
  const [starterSeed, setStarterSeed] = useState<SegmentBuilderStarterSeed | null>(
    null,
  );

  const loadAudiences = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res: any = await apiService.get("/api/admin/segments");
      const rows: AudienceRow[] =
        res?.data?.segments ?? res?.segments ?? res?.data ?? [];
      setAudiences(rows);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load audiences");
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAudiences();
  }, [loadAudiences]);

  const archivedCount = useMemo(
    () => audiences.filter((a) => !a.is_active).length,
    [audiences],
  );

  const filteredRows = useMemo(() => {
    // Archived audiences used to appear silently inside All/System/Custom with
    // no visual marker at all. They now live behind their own tab.
    let rows =
      filter === "archived"
        ? audiences.filter((a) => !a.is_active)
        : audiences.filter((a) => a.is_active);
    if (filter === "sequence") rows = rows.filter((a) => a.linked_flow_slug);
    if (filter === "system") rows = rows.filter((a) => a.is_system);
    if (filter === "custom") rows = rows.filter((a) => !a.is_system);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (a) =>
          a.slug.toLowerCase().includes(q) ||
          a.name.toLowerCase().includes(q) ||
          (a.description?.toLowerCase() ?? "").includes(q),
      );
    }
    // Sequence-linked audiences first — they are the ones tied to something running.
    return [...rows].sort((a, b) => {
      const al = a.linked_flow_slug ? 0 : 1;
      const bl = b.linked_flow_slug ? 0 : 1;
      if (al !== bl) return al - bl;
      return (a.linked_flow_name || a.name).localeCompare(b.linked_flow_name || b.name);
    });
  }, [audiences, filter, search]);

  /**
   * Archive / restore. System audiences cannot be deleted (the API refuses),
   * so is_active is the lifecycle control — and archived audiences drop out of
   * the Compose & Send picker automatically, which is the point.
   */
  const setActive = useCallback(
    async (row: AudienceRow, next: boolean) => {
      setBusyId(row.id);
      try {
        await apiService.put(`/api/admin/segments/${row.id}`, { is_active: next });
        await loadAudiences({ silent: true });
      } catch (e: any) {
        setError(e?.message ?? "Could not update the audience");
      } finally {
        setBusyId(null);
      }
    },
    [loadAudiences],
  );

  const handleStarterSelect = (starter: StarterEntry) => {
    setChooserOpen(false);
    const raw =
      starter.filterJson ??
      (starter as { filter_json?: unknown }).filter_json;
    // Renamed from `filter`: that shadowed the tab-filter state of the same
    // name for the whole handler body.
    const starterFilter = normalizeStarterFilter(raw);
    const seed: SegmentBuilderStarterSeed = {
      name: starter.name,
      description:
        typeof starter.description === "string" ? starter.description : undefined,
      filter: starterFilter,
    };
    setStarterSeed(seed);
    setSegmentBuilderOpen(true);
  };

  const handleCloseSegmentBuilder = () => {
    setSegmentBuilderOpen(false);
    setStarterSeed(null);
  };

  return (
    <PillarPageShell
      pillar="marketing"
      title="Audiences"
      subtitle="Saved member groups for targeting marketing sends. Counts are deliverable reach — unsubscribed, bounced and opted-out Members are excluded."
      helpTipId="audience.kind_marketing"
      rightActions={
        <ActionButton
          verb="duplicate"
          labelOverride="+ Build an Audience"
          onClick={() => setChooserOpen(true)}
          showIcon={false}
          variantOverride="primary"
        />
      }
    >
      {/* Filter tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={filter} onChange={(_, v) => setFilter(v)}>
          {FILTERS.map((f) => (
            <Tab
              key={f.id}
              value={f.id}
              label={f.id === "archived" && archivedCount ? `${f.label} (${archivedCount})` : f.label}
              sx={{ fontWeight: 600, fontSize: "0.85rem" }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name or slug…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={140} />
            </Grid>
          ))}
        </Grid>
      ) : filteredRows.length === 0 ? (
        <EmptyState
          surface="audiences"
          onCreate={() => setChooserOpen(true)}
        />
      ) : (
        <Grid container spacing={2}>
          {filteredRows.map((row) => (
            <Grid item xs={12} sm={6} md={4} key={row.id}>
              <Card
                sx={{
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 700,
                        color: "#1A5F7A",
                        fontSize: "1.05rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {row.name}
                    </Typography>
                    <Chip
                      label={row.is_system ? "System" : "Custom"}
                      size="small"
                      sx={{
                        bgcolor: row.is_system ? "#5D6D7E" : "#D4AC0D",
                        color: row.is_system ? "white" : "#1B1F23",
                        fontSize: "0.65rem",
                        height: 20,
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      color: "text.secondary",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    {row.slug}
                  </Typography>
                  {row.description && (
                    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.4 }}>
                      {row.description}
                    </Typography>
                  )}
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#1A5F7A",
                      fontWeight: 700,
                      mt: 1,
                    }}
                  >
                    {row.last_refreshed_at == null
                      ? "—"
                      : (row.reachable_count ?? row.last_count)?.toLocaleString() ??
                        "0"}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ color: "text.secondary", ml: 0.5 }}
                    >
                      {row.last_refreshed_at == null
                        ? "not materialized yet — open and refresh"
                        : "members"}
                      {row.last_refreshed_at != null &&
                      row.reachable_count != null &&
                      row.last_count != null &&
                      row.reachable_count !== row.last_count
                        ? ` (${row.last_count.toLocaleString()} matched)`
                        : ""}{" "}
                      <HelpTip id="audience.live_count" size={12} />
                    </Typography>
                  </Typography>
                </CardContent>
                <Box
                  sx={{
                    p: 1.5,
                    pt: 0,
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {/* The audience↔sequence link, both ways: this chip goes to the
                      Funnel Manager, and each funnel card links back here. */}
                  {row.linked_flow_slug && (
                    <Tooltip
                      title={`This audience mirrors the entry rule of ${row.linked_flow_name || row.linked_flow_slug}. Open it in the Funnel Manager.`}
                      arrow
                    >
                      <Chip
                        size="small"
                        label={`Feeds ${flowBadge(row.linked_flow_name) || "sequence"} →`}
                        onClick={() =>
                          navigate(
                            `/dashboard/communications/marketing/funnels?flow=${encodeURIComponent(row.linked_flow_slug!)}`,
                          )
                        }
                        sx={{
                          height: 22,
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          bgcolor: "#F0F7FA",
                          color: "#1A5F7A",
                          border: "1px solid #E3D9C6",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  )}
                  <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
                    {row.is_active ? (
                      <Tooltip
                        title={
                          row.linked_flow_slug
                            ? "This audience mirrors a live sequence — archiving it only hides it from pickers, the sequence keeps enrolling"
                            : "Hide from campaign pickers. Nothing is deleted."
                        }
                        arrow
                      >
                        <span>
                          <ActionButton
                            verb="pause"
                            labelOverride="Archive"
                            size="small"
                            showIcon={false}
                            disabled={busyId === row.id}
                            onClick={() => setActive(row, false)}
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <ActionButton
                        verb="resume"
                        labelOverride="Restore"
                        size="small"
                        showIcon={false}
                        disabled={busyId === row.id}
                        onClick={() => setActive(row, true)}
                      />
                    )}
                    <ActionButton
                      verb="see_recipients"
                      labelOverride="View"
                      size="small"
                      showIcon={false}
                      onClick={() =>
                        navigate(
                          `/dashboard/communications/marketing/audiences/${encodeURIComponent(row.id)}`,
                        )
                      }
                    />
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <StarterChooser
        entityType="audience"
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onSelect={handleStarterSelect}
      />

      <SegmentBuilderDialog
        open={segmentBuilderOpen}
        onClose={handleCloseSegmentBuilder}
        starterSeed={starterSeed}
        onSaved={() => void loadAudiences({ silent: true })}
      />
    </PillarPageShell>
  );
};

export default MarketingAudiencesPage;
